/**
 * Label × severity cross-product utilities for the moderation work queue.
 *
 * Generates `LabelSeverityOption[]` from `CombinedLabel[]` for the MultiSelect
 * dropdown, and provides a parser for resolving encoded `"labelId:severity"`
 * strings back to display text (e.g. `"SHS 2"`).
 *
 * @see ../domain/ModerationLabels.ts — LabelSeverityOption, CombinedLabel
 * @see ../../internal-audit/components/AuditDecisionPanel/AuditDecisionPanel.tsx — reference pattern (lines 111-132)
 */

import type { StandardLabel } from '@core/graphql/types/schema';

import type { CombinedLabel, LabelSeverityOption } from '../domain/ModerationLabels';

/** Default maximum severity when no explicit value is available */
const DEFAULT_MAX_SEVERITY = 3;

/**
 * Content type sort order for grouping labels by type in the panel.
 * Text labels appear first, then Image, then Custom.
 * Unknown types sort last.
 */
const CONTENT_TYPE_ORDER: Record<string, number> = {
  Text: 0,
  Image: 1,
  Custom: 2,
};

/** Returns the numeric sort weight for a content type string. */
function getContentTypeSortOrder(contentType: string): number {
  return CONTENT_TYPE_ORDER[contentType] ?? 99;
}

/**
 * Determines whether a label should be restricted to severity 1 only.
 * Image labels and custom labels always use a single severity level.
 */
function isSingleSeverityLabel(contentType: string, source: CombinedLabel['source']): boolean {
  return contentType.toLowerCase() === 'image' || source === 'custom';
}

/**
 * Generates a cross-product of labels × severity levels for the moderation
 * expanded label list.
 *
 * - Standard labels: severity 1 through `maxSeverity` (default 3)
 * - Standard image labels (`contentType === 'Image'`): severity 1 only
 * - Custom labels: severity 1 only (treated like image labels)
 *
 * Each option encodes its value as `"labelId:severity"` so it flows through the
 * session reducer and GraphQL mutation unchanged — the BFF parses them server-side.
 *
 * @param labels - Combined standard + custom labels from `useCombinedLabels()`
 * @returns Sorted `LabelSeverityOption[]` — grouped by content type (Text → Image → Custom), then alphabetically by name, then ascending severity
 */
export function generateLabelSeverityOptions(labels: CombinedLabel[]): LabelSeverityOption[] {
  const options: LabelSeverityOption[] = [];

  for (const label of labels) {
    // Determine maxSeverity from the original StandardLabel; custom labels default to 3
    const maxSeverity = label.source === 'standard' ? ((label.original as StandardLabel).maxSeverity ?? DEFAULT_MAX_SEVERITY) : DEFAULT_MAX_SEVERITY;

    // Determine content type — standard labels carry it; custom labels get 'Custom'
    const contentType = label.source === 'custom' ? 'Custom' : (label.contentType ?? 'Text');

    // Image and custom labels are restricted to severity 1 only
    const singleSeverity = isSingleSeverityLabel(contentType, label.source);
    const severityCount = singleSeverity ? 1 : maxSeverity;

    for (let severity = 1; severity <= severityCount; severity++) {
      // Single-severity labels: display just the name (severity is always 1, redundant)
      // Text labels: display "LABEL_NAME SEVERITY"
      const displayLabel = singleSeverity ? label.name.toUpperCase() : `${label.name.toUpperCase()} ${severity}`;

      options.push({
        id: `${label.id}:${severity}`,
        value: `${label.id}:${severity}`,
        label: displayLabel,
        labelId: label.id,
        labelName: label.name,
        severity,
        contentType,
        source: label.source,
      });
    }
  }

  // Sort: by content type (Text → Image → Custom), then alphabetically by name, then ascending by severity
  return options.sort(
    (a, b) =>
      getContentTypeSortOrder(a.contentType) - getContentTypeSortOrder(b.contentType) || a.labelName.localeCompare(b.labelName) || a.severity - b.severity
  );
}

/**
 * Parses an encoded `"labelId:severity"` string and resolves it to a
 * human-readable display string like `"SHS 2"`.
 *
 * Image labels display just the name without severity (e.g. `"ALCOHOL"`),
 * since they always have severity 1 and the number is redundant.
 *
 * Falls back to the raw encoded value if the label ID cannot be resolved.
 *
 * @param encoded - The encoded label string, e.g. `"uuid-123:2"`
 * @param labels  - The combined labels list for name lookups
 * @returns Display text, e.g. `"SHS 2"` or `"ALCOHOL"`, or the raw value on lookup failure
 */
export function parseLabelSeverityDisplay(encoded: string, labels: CombinedLabel[]): string {
  const colonIndex = encoded.lastIndexOf(':');

  if (colonIndex > 0) {
    const labelId = encoded.substring(0, colonIndex);
    const severity = encoded.substring(colonIndex + 1);
    const label = labels.find(l => l.id === labelId);

    if (label) {
      // Image and custom labels: omit severity (always 1, redundant)
      const singleSeverity = isSingleSeverityLabel(label.contentType ?? 'Text', label.source);

      return singleSeverity ? label.name.toUpperCase() : `${label.name.toUpperCase()} ${severity}`;
    }
  }

  // Fallback: return the raw value (backward compatibility)
  return encoded;
}
